import React, { useState, useCallback, useMemo } from 'react'
import { useWorkspace } from '../../../core/workspace/WorkspaceContext'
import { useMantineColorScheme } from '@mantine/core'
import {
  Box,
  Button,
  Text,
  Title,
  Paper,
  Stack,
  Group,
  TextInput,
  ActionIcon,
  Loader,
  Menu,
  Tooltip
} from '@mantine/core'
import {
  IconPlus,
  IconTrash,
  IconEdit,
  IconChevronDown,
  IconChevronRight,
  IconFolderPlus,
  IconDots,
  IconCopy,
  IconArrowRight
} from '@tabler/icons-react'
import { eventBus } from '../../../core/event-bus'

export interface PageTreeProps {
  phase?: number
  currentPageId?: string
  onPageSelect?: (pageId: string) => void
  onPageCreate?: (parentId?: string) => void
  onPageDelete?: (pageId: string) => void
  onPageRename?: (pageId: string, newName: string) => void
}

export interface PageItemProps {
  page: {
    id: string
    title: string
    parentId?: string
    type?: string
  }
  isSelected?: boolean
  depth?: number
  onSelect?: () => void
  onDelete?: () => void
  onRename?: (newName: string) => void
  onCreateChild?: () => void
  hasChildren?: boolean
}

export const PageItem = React.forwardRef<HTMLDivElement, PageItemProps>(({
  page,
  isSelected,
  depth = 0,
  onSelect,
  onDelete,
  onRename,
  onCreateChild,
  hasChildren
}, ref) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(page.title)
  const [showChildren, setShowChildren] = useState(true)
  const [isHovered, setIsHovered] = useState(false)

  const handleRename = () => {
    if (editName.trim() && editName !== page.title) {
      onRename?.(editName.trim())
    }
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleRename()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditName(page.title)
    }
  }

  return (
    <Box
      ref={ref}
      className={`page-item ${isSelected ? 'selected' : ''}`}
      style={{
        position: 'relative',
        marginLeft: depth > 0 ? 8 : 0
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        className="page-item-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 8px',
          borderRadius: 4,
          backgroundColor: isSelected ? '#e3e5e8' : isHovered ? '#f7f6f3' : 'transparent',
          cursor: 'pointer',
          transition: 'background-color 0.1s ease',
          minHeight: 28
        }}
        onClick={onSelect}
      >
        {/* Expand/collapse toggle */}
        <Box style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {hasChildren ? (
            <ActionIcon
              variant="subtle"
              size="xs"
              onClick={(e) => {
                e.stopPropagation()
                setShowChildren(!showChildren)
              }}
              style={{ padding: 0 }}
            >
              {showChildren ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
            </ActionIcon>
          ) : (
            <Box style={{ width: 12 }} />
          )}
        </Box>

        {/* Page icon */}
        <Box style={{ fontSize: 14 }}>
          {page.type === 'database' ? '📋' : '📄'}
        </Box>

        {/* Page title */}
        <Box style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <TextInput
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleRename}
              autoFocus
              size="xs"
              variant="filled"
              styles={{
                input: {
                  padding: '2px 8px',
                  height: 24,
                  fontSize: 13
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <Text
              size="sm"
              fw={isSelected ? 600 : 400}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {page.title}
            </Text>
          )}
        </Box>

        {/* Action buttons */}
        {(isHovered || isSelected) && !isEditing && (
          <Group gap={2} style={{ flexShrink: 0 }}>
            <Tooltip label="Add subpage">
              <ActionIcon
                variant="subtle"
                size="xs"
                color="blue"
                onClick={(e) => {
                  e.stopPropagation()
                  onCreateChild?.()
                }}
              >
                <IconPlus size={12} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="More options">
              <Menu position="bottom-start" shadow="md">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconDots size={12} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEdit size={14} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditing(true)
                    }}
                  >
                    Rename
</Menu.Item>
                  <Menu.Item
                    leftSection={<IconCopy size={14} />}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Duplicate
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    color="red"
                    leftSection={<IconTrash size={14} />}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete?.()
                    }}
                  >
                    Delete
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Tooltip>
          </Group>
        )}
      </Box>

      {/* Children are rendered by parent */}
      {!showChildren && hasChildren && (
        <Box style={{ fontSize: 12, color: '#8e8e8e', paddingLeft: 32, paddingTop: 2 }}>
          {hasChildren ? '...' : ''}
        </Box>
      )}
    </Box>
  )
});

PageItem.displayName = 'PageItem';

export const PageTree: React.FC<PageTreeProps> = ({
  currentPageId,
  onPageSelect,
  onPageCreate
}) => {
  const { pages, createPage, deletePage, renamePage, selectPage, getChildPages, hasChildren } = useWorkspace()
  const { colorScheme } = useMantineColorScheme()
  const [loading, setLoading] = useState(false)
  
  // Dark mode colors
  const bgColor = colorScheme === 'dark' ? '#292929' : '#ffffff'
  const textColor = colorScheme === 'dark' ? '#e8eaed' : '#202124'
  const mutedColor = colorScheme === 'dark' ? '#9aa0a6' : '#8e8e8e'
  const borderColor = colorScheme === 'dark' ? '#3c4043' : '#e9e9e7'

  // Build hierarchical page structure
  const pageHierarchy = useMemo(() => {
    const topLevel = pages.filter(p => !p.parentId)
    return topLevel
  }, [pages])

  const handleCreatePage = async (parentId?: string) => {
    try {
      setLoading(true)
      const newPage = await createPage('Untitled', parentId)
      await selectPage(newPage.id)
      onPageSelect?.(newPage.id)
    } catch (error) {
      console.error('Failed to create page:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeletePage = async (pageId: string) => {
    if (!confirm('Delete this page? This cannot be undone.')) return
    
    try {
      await deletePage(pageId)
      // Select first available page if we deleted current page
      if (currentPageId === pageId && pages.length > 0) {
        const remaining = pages.filter(p => p.id !== pageId)
        if (remaining.length > 0) {
          await selectPage(remaining[0].id)
        }
      }
    } catch (error) {
      console.error('Failed to delete page:', error)
    }
  }

  const handleRenamePage = async (pageId: string, newName: string) => {
    try {
      await renamePage(pageId, newName)
    } catch (error) {
      console.error('Failed to rename page:', error)
    }
  }

  // Recursive render for page tree
  const renderPage = (page: typeof pages[0], depth: number = 0): React.ReactNode => {
    const childPages = getChildPages(page.id)
    const hasKids = childPages.length > 0
    const pageChildren = hasChildren(page.id)

    return (
      <Box key={page.id}>
        <PageItem
          page={page}
          isSelected={page.id === currentPageId}
          depth={depth}
          onSelect={() => {
            selectPage(page.id)
            onPageSelect?.(page.id)
          }}
          onDelete={() => handleDeletePage(page.id)}
          onRename={(newName) => handleRenamePage(page.id, newName)}
          onCreateChild={() => handleCreatePage(page.id)}
          hasChildren={pageChildren}
        />
        {childPages.length > 0 && (
          <Box style={{ borderLeft: `1px solid ${borderColor}`, marginLeft: 14 }}>
            {childPages.map(child => renderPage(child, depth + 1))}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box className="page-tree" style={{ padding: '8px 12px', backgroundColor: bgColor, color: textColor }}>
      {/* Quick Actions */}
      <Box mb="md">
        <Button
          variant="subtle"
          size="xs"
          fullWidth
          leftSection={<IconPlus size={14} />}
          onClick={() => handleCreatePage()}
          styles={{
            root: {
              justifyContent: 'flex-start',
              paddingLeft: 8,
              color: textColor
            }
          }}
        >
          New page
        </Button>
      </Box>

      {/* Page List */}
      <Box className="page-tree-content">
        {pageHierarchy.length > 0 ? (
          pageHierarchy.map(page => renderPage(page))
        ) : (
          <Paper
            p="lg"
            radius="md"
            style={{
              textAlign: 'center',
              background: '#f7f6f3',
              border: 'none'
            }}
          >
            <Stack gap="sm" align="center">
              <Text size="sm" c="dimmed">No pages yet</Text>
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => handleCreatePage()}
              >
                Create first page
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  )
}

export default PageTree